import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMenuStore } from '../zustand/store/useMenuStore';
import { useShallow } from 'zustand/shallow';
import useQueryLinkNavigate from '../hooks/useQueryLinkNavigate';
import { useIs17Inch } from '../hooks/useBreakPoint';

import '../css/side-bar.css';
import { useDynamicStyle } from '../hooks/useDynamicStyle';
import * as Icons from '../images/icons/icons_component'
import * as Theme from '../images/icons/icons_theme';

function MenuList({topList, subList, title, topMenuCode, isSidebarToggle, $isActive, $goBackAction, graySubIcon, whiteSubIcon, hoverSubIcon, handleSmallMoniterMenuClick}) {

    const [ clickedCategory, collapsed, setCollapsed ] = useMenuStore(useShallow((state) => [state.clickedCategory, state.collapsed, state.setCollapsed]));
    const { checkedTopCode } = useMenuStore((state) => state.currentInfo);

    const navigate = useNavigate();
    const { basePath } = useQueryLinkNavigate();

    // useRef current에 담긴 엘리먼트 바깥(menu 선택 외 영역)을 클릭 시 드롭메뉴 닫힘
    const dropMenuRef = useRef(null);
    const topMenuRef = useRef(null);

    useEffect(() => {
        const handleOutsideClose = (e) => {
            if(!isSidebarToggle && !topMenuRef.current.contains(e.target)) {
                dropMenuRef.current.classList.remove('visible');
            };
            if(!isSidebarToggle && collapsed === topMenuCode && topMenuRef.current.contains(e.target)) {
                dropMenuRef.current.classList.add('visible');
            };
        };

        // ESC 키 누르면 하위 메뉴 닫히고 상위 메뉴로 포커스
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                const isLayerOpen = dropMenuRef.current?.classList.contains('visible');
                
                if (isLayerOpen) {
                    e.preventDefault();
                    dropMenuRef.current.classList.remove('visible');
                    
                    if (topMenuRef.current) {
                        topMenuRef.current.focus();
                    }
                }
            }
        };

        // 탭 키로 다른 구역(다음 top_menu 등)으로 포커스가 빠져나갔을 때 자동으로 닫는 로직
        const handleFocusIn = (e) => {
            if (topMenuRef.current && dropMenuRef.current) {
                const isLayerOpen = dropMenuRef.current.classList.contains('visible');
                
                if (isLayerOpen) {
                    // 새로 포커스를 받은 타겟이 내 상위메뉴 버튼 안쪽 여부
                    const insideTopMenu = topMenuRef.current.contains(e.target);
                    // 새로 포커스를 받은 타겟이 내 하위메뉴 레이어 안쪽 여부
                    const insideDropMenu = dropMenuRef.current.contains(e.target);

                    // 다음 탑메뉴 버튼이나 본문 영역으로 포커스가 넘어갈 때
                    if (!insideTopMenu && !insideDropMenu) {
                        dropMenuRef.current.classList.remove('visible');
                    }
                }
            }
        };
        
        document.addEventListener('click', handleOutsideClose);
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('focusin', handleFocusIn);
        
        return () => {
            document.removeEventListener('click', handleOutsideClose);
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('focusin', handleFocusIn);
        }
    }, [collapsed, isSidebarToggle]);

    // Dynamic Style Hook
    const { backColor, hoverBackColor, fontColor, hoverFontColor, lightBackColor, darkBackColor, lightBorderShadowInColor, darkBorderShadowColor } = useDynamicStyle();

    // Collapsed Toggle Dynamic Style
    const collapsedTopMenu = collapsed === topMenuCode;
    const menuHeight = $isActive || $goBackAction ? subList.length*1.73 + (!isSidebarToggle ? 1.78 : 0) : 0;
    const collapsedMenu = {maxHeight: `${menuHeight}rem`};

    // sub menu 없는 경우
    const depth_n = topList !== undefined;
    const clickedTopMenu = topList?.MENUSORC === basePath;

    const selectStyle = fontColor + ' bold rounded ' + lightBorderShadowInColor + (depth_n ? ' pl-2 ' : ' ') + backColor;

    // 카테고리별 아이콘 색상 클래스 접두어 (관리·홈은 ho 공용)
    const theme = clickedCategory === 3 ? 'st' : clickedCategory === 21 ? 'tr' : 'ho';
    const is17Inch = useIs17Inch();

    const firstSubMenuRefs = useRef({});
    
    const menuLink = (type, menu) => {
        if(type === 'top') {
            if(depth_n) {
                navigate(topList?.MENUSORC);
                handleSmallMoniterMenuClick();
            } else {
                setCollapsed(topMenuCode);

                setTimeout(() => {
                    if (firstSubMenuRefs.current && topMenuCode) {
                        const targetEl = firstSubMenuRefs.current[topMenuCode];
                        if (targetEl) {
                            targetEl.focus();
                        }
                    }
                }, 100);
            }
        } else {
            navigate(menu?.MENUSORC);
            if (type === 'open') {
                handleSmallMoniterMenuClick();
            } else if (type === 'close') {
                dropMenuRef.current.classList.remove('visible');
            }
        }
    };

    return (
        <>
            {isSidebarToggle ?
            <>
                <button className={`top_menu h_icon_box ${depth_n ? 'py-1-1 ' + (clickedTopMenu ? selectStyle : 'co_gray') : 'depth_y'} ${hoverFontColor}`}
                    onClick={() => menuLink('top')}>
                        <div className={`menu_title_text ${($isActive || $goBackAction || clickedTopMenu) && 'bold'}`}>
                            <div className='icon'>{depth_n ? (clickedTopMenu ? hoverSubIcon : graySubIcon) : graySubIcon}</div>
                            <div className='hover_icon'>{hoverSubIcon}</div>
                            {depth_n ? topList?.MENUNAME : title}
                        </div>
                        {!depth_n && (collapsedTopMenu || checkedTopCode === topMenuCode ? <Icons.CollapseIco/> : <Icons.ExpandIco/>)}
                </button>
                <ul style={collapsedMenu} className={`sub_menu_container ${$isActive || $goBackAction ? 'overflow-hidden' : 'invisible'}`}>
                    {subList && subList.map((menu) => {
                        const clickedSubMenu = menu.MENUSORC === basePath;
                        return (
                            <div className='sub_menu_wrapper rounded' key={menu.MENUFLAG}>
                                <div role='link' tabIndex={collapsedTopMenu || checkedTopCode ? 0 : -1}
                                    className={`sub_menu_list ${clickedSubMenu ? selectStyle : 'co_gray'}`}
                                    onClick={() => menuLink('open', menu)}
                                    onKeyDown={(e) => {
                                        if(e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            menuLink('open', menu);
                                        }
                                    }}>
                                    <div className={clickedSubMenu ? '' : 'menu_here'}>{clickedSubMenu ? <Theme.Here className={`${theme}_primary`}/> : <Theme.PageNext className='co_light'/>}</div>
                                    {menu.MENUNAME}
                                </div>
                            </div>
                        );
                    })}
                </ul>
            </>
            :
            <>
                <div className={`top_menu close_tooltip justify-self-center ${is17Inch ? 'p-1' : 'p-2'} ${(clickedTopMenu || $goBackAction) && darkBackColor}`}>
                    <button ref={topMenuRef} aria-haspopup='true' aria-expanded={dropMenuRef.current?.classList.contains('visible')} className={`menu_title_text h_icon_box tooltip_box`} onClick={() => menuLink('top')}>
                        <div className='icon'>{depth_n ? (clickedTopMenu ? whiteSubIcon : graySubIcon) : ($goBackAction ? whiteSubIcon : graySubIcon)}</div>
                        <div className='hover_icon'>{depth_n ? (clickedTopMenu ? whiteSubIcon : hoverSubIcon) : ($goBackAction ? whiteSubIcon : hoverSubIcon)}</div>
                        <span className={`tooltip ${darkBackColor}`}>{depth_n ? topList?.MENUNAME : title}</span>
                    </button>
                    <div ref={dropMenuRef} style={collapsedMenu} className={`close_sub_menu_container ${$isActive || $goBackAction ? darkBorderShadowColor : ''}`}>
                        <h6 className={`sub_menu_title cursor-default ${fontColor} ${lightBackColor}`}>{title}</h6>
                        {subList && subList.map((menu, index) => {
                            const clickedSubMenu = menu.MENUSORC === basePath;
                            const isFirstChild = index === 0;
                            return (
                                <div className='sub_menu_wrapper' key={menu.MENUFLAG}>
                                    <div className={`sub_menu_list sub_menu_side_close ${clickedSubMenu ? darkBackColor + ' co_white bold' : 'co_gray'} ${!clickedSubMenu && hoverBackColor + ' ' + hoverFontColor}`}
                                        ref={el => {if (isFirstChild && el) {
                                            firstSubMenuRefs.current[topMenuCode] = el;
                                        }}}
                                        role='link' tabIndex={0} onClick={() => menuLink('close', menu)}
                                        onKeyDown={(e) => {
                                            if(e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                menuLink('close', menu);
                                            }
                                        }}>
                                        <div className={clickedSubMenu ? '' : 'menu_here'}>{clickedSubMenu ? <Theme.Here16 className='co_white'/> : <Theme.Here16 className={`${theme}_primary`}/>}</div>
                                        {menu.MENUNAME}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </>
            }
        </>
    )
}

export default MenuList